import { useRouter } from 'next/router'
import { useCallback, useEffect, useRef, useState } from 'react'

const errorMessage = 'Please ignore this error.'

export const useBlockNavigation = (shouldBlock: boolean) => {
  const router = useRouter()
  const hasConfirmed = useRef(false)
  const nextPath = useRef('')
  const [isBlockingNavigation, setIsBlockingNavigation] = useState(false)
  const cancelNavigation = useCallback(() => {
    setIsBlockingNavigation(false)
    nextPath.current = ''
  }, [])
  const confirmNavigation = useCallback(() => {
    setIsBlockingNavigation(false)
    hasConfirmed.current = true

    return router.push(nextPath.current)
  }, [router])

  useEffect(() => {
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      if (shouldBlock) {
        event.preventDefault()
      }
    }
    const onRouteChange = (url: string) => {
      if (shouldBlock && !hasConfirmed.current) {
        setIsBlockingNavigation(true)
        nextPath.current = url
        router.events.emit('routeChangeError', '', '', { shallow: false })
        throw errorMessage
      }
    }
    const rejectionHandler = (event: PromiseRejectionEvent) => {
      if (event.reason === errorMessage) {
        event.preventDefault()
      }
    }

    router.events.on('routeChangeStart', onRouteChange)

    window.addEventListener('unhandledrejection', rejectionHandler)
    window.addEventListener('beforeunload', onBeforeUnload)

    return () => {
      router.events.off('routeChangeStart', onRouteChange)

      window.removeEventListener('unhandledrejection', rejectionHandler)
      window.removeEventListener('beforeunload', onBeforeUnload)
    }
  }, [router.events, shouldBlock])

  return { cancelNavigation, confirmNavigation, isBlockingNavigation }
}
